import { useApi, useToaster } from "@shopexperts/shared";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, type Ref, toValue, watch } from "vue";

interface UseAdminDraftLeadsOptions {
    /** Filter by status: pending, submitted, or all (default) */
    status?: Ref<"pending" | "submitted" | "all"> | "pending" | "submitted" | "all";
    /** Number of items per page */
    limit?: number;
}

/**
 * Composable for fetching paginated list of draft leads (admin)
 * 
 * @example
 * ```ts
 * const status = ref<'pending' | 'submitted' | 'all'>('pending');
 * const { draftLeads, isLoading, hasNextPage, fetchNextPage } = useAdminDraftLeads({ status });
 * ```
 */
export function useAdminDraftLeads(options: UseAdminDraftLeadsOptions = {}) {
    const api = useApi();
    const toaster = useToaster();

    const { status = "all", limit = 15 } = options;

    const statusValue = computed(() => toValue(status));

    const {
        data,
        isLoading,
        error,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    } = useInfiniteQuery({
        queryKey: computed(() => ["admin", "draft-leads", { status: statusValue.value }]),
        queryFn: async ({ pageParam }) => {
            return await api.admin.draftLeads.listDraftLeads({
                query: {
                    limit,
                    cursor: pageParam,
                    status: statusValue.value === "all" ? undefined : statusValue.value,
                },
            });
        },
        getNextPageParam: (lastPage) => {
            return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
        },
        initialPageParam: undefined as string | undefined,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const draftLeads = computed(() => {
        return data.value?.pages.flatMap((page) => page.data) || [];
    });

    const totalCount = computed(() => {
        return draftLeads.value.length;
    });

    watch(error, (err) => {
        if (err) {
            toaster.create({
                title: "Failed to load draft leads",
                type: "error",
            });
            console.error("[useAdminDraftLeads] Error:", err);
        }
    });

    return {
        draftLeads,
        totalCount,
        isLoading,
        error,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    };
}
